import { useState } from "react";
import { UtensilsCrossed, Flower2, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { IMG } from "@/lib/images";
import { SERVICES, CHEF_EXPERIENCE_TIMES } from "@/lib/services";
import Reveal, { Caption } from "@/components/Reveal";
import ServiceRequestModal from "@/components/ServiceRequestModal";
import FoodDiningSection from "@/components/FoodDiningSection";
import FoodRequestModal from "@/components/FoodRequestModal";

export default function ServicesPage() {
  const { t } = useLang();
  const [requestService, setRequestService] = useState(null);
  const [foodOpen, setFoodOpen] = useState(false);

  return (
    <div data-testid="services-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-20 sm:pt-28 pb-10">
        <Reveal>
          <Caption>{t("services.caption")}</Caption>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 text-ink max-w-3xl">
            {t("services.title")}
          </h1>
          <p className="text-ink/60 leading-relaxed mt-6 max-w-2xl text-base sm:text-lg">{t("services.copy")}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-24 space-y-10">
        {SERVICES.map((s, idx) => {
          const isChef = s.id === "chef";
          const accent = isChef ? "terracotta" : "teal";
          return (
            <Reveal key={s.id} delay={0.08 * idx}>
              <article
                data-testid={`service-card-${s.id}`}
                className="bg-white rounded-[2rem] border border-terracotta/10 overflow-hidden grid grid-cols-1 lg:grid-cols-5"
              >
                <div className="lg:col-span-2 relative min-h-[260px]">
                  <img
                    src={isChef ? IMG.seafood : IMG.gardenPath}
                    alt={t(`services.${s.id}.name`)}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <span className={`absolute top-5 left-5 rounded-full bg-night/70 backdrop-blur text-sand px-4 py-1.5 text-xs font-mono uppercase tracking-[0.15em]`}>
                    {t("services.priceNote")}
                  </span>
                </div>
                <div className="lg:col-span-3 p-7 sm:p-10">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${isChef ? "bg-terracotta/10 text-terracotta" : "bg-teal/10 text-teal"}`}>
                      {isChef ? <UtensilsCrossed size={21} /> : <Flower2 size={21} />}
                    </span>
                    <h2 className="font-serif text-2xl sm:text-3xl text-ink leading-tight">{t(`services.${s.id}.name`)}</h2>
                  </div>
                  <p className="text-ink/60 text-sm sm:text-base leading-relaxed mb-7">{t(`services.${s.id}.tagline`)}</p>

                  {isChef ? (
                    <div className="space-y-6">
                      <div>
                        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 mb-3">{t("services.chef.gatheringLbl")}</p>
                        <ul className="space-y-2">
                          {s.gatherings.map((g) => (
                            <li key={g.id} className="flex items-baseline justify-between gap-4 border-b border-ink/5 pb-2" data-testid={`chef-gathering-${g.id}`}>
                              <span className="text-sm font-medium">{t(`services.chef.gatherings.${g.id}`)} · {g.guests} {t("services.guestsSuffix")}</span>
                              <span className="text-xs text-gold font-semibold whitespace-nowrap">{t("services.priceNote")}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 mb-3">{t("services.chef.experienceLbl")}</p>
                        <ul className="space-y-2">
                          {s.experiences.map((x) => (
                            <li key={x} className="flex items-baseline justify-between gap-4 border-b border-ink/5 pb-2" data-testid={`chef-experience-${x}`}>
                              <span className="text-sm font-medium">{t(`services.chef.experiences.${x}`)}</span>
                              {CHEF_EXPERIENCE_TIMES[x] && <span className="text-xs text-ink/45 whitespace-nowrap">{CHEF_EXPERIENCE_TIMES[x]}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 mb-3">{t("services.massage.durationLbl")}</p>
                      <ul className="space-y-2">
                        {s.durations.map((d) => (
                          <li key={d} className="flex items-baseline justify-between gap-4 border-b border-ink/5 pb-2" data-testid={`massage-duration-${d}`}>
                            <span className="text-sm font-medium">{t(`services.massage.durations.${d}`)}</span>
                            <span className="text-xs text-gold font-semibold whitespace-nowrap">{t("services.priceNote")}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    data-testid={`service-request-btn-${s.id}`}
                    onClick={() => setRequestService(s.id)}
                    className={`mt-8 rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 transition-colors bg-night text-sand ${isChef ? "hover:bg-terracotta" : "hover:bg-teal"}`}
                  >
                    {t("services.requestBtn")} <ArrowUpRight size={16} />
                  </button>
                </div>
              </article>
            </Reveal>
          );
        })}
      </section>

      <FoodDiningSection onRequest={() => setFoodOpen(true)} />

      <ServiceRequestModal serviceId={requestService} open={!!requestService} onClose={() => setRequestService(null)} />
      <FoodRequestModal open={foodOpen} onClose={() => setFoodOpen(false)} />
    </div>
  );
}
